import { ChangeEvent, FormEvent, FunctionComponent, ReactElement, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Stack } from '@mui/material';
import PasswordField from '@/components/ui/form-inputs/PasswordField';
import LoadingButton from '@/components/ui/loading-button';
import { isValidEmail, requiredField } from '@/shared/utils/validation';
import { INVALID_CREDENTIALS, INVALID_EMAIL } from '@/shared/utils/validation/constants';
import { useLogin } from '@/queries/auth';
import TextField from '@/components/ui/form-inputs/TextField';
import { LOGIN_FORM_KEY } from '@/shared/constants/form-keys';
import AuthCardLayout from '../layouts/auth-card-layout/AuthCardLayout';
import AuthLayout from '../layouts/auth-layout/AuthLayout';

const Login: FunctionComponent = (): ReactElement => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');

	const [emailError, setEmailError] = useState('');
	const [passwordError, setPasswordError] = useState('');

	const { isLoading, mutate: onLogin } = useLogin({
		onError: () => {
			setEmailError(INVALID_CREDENTIALS);
			setPasswordError(INVALID_CREDENTIALS);
		},
	});

	const validateEmail = (): boolean => {
		if (email.length === 0) {
			setEmailError(requiredField('Email'));
			return false;
		}

		if (!isValidEmail(email)) {
			setEmailError(INVALID_EMAIL);
			return false;
		}

		setEmailError('');
		return true;
	};

	const validatePassword = (): boolean => {
		if (password.length === 0) {
			setPasswordError(requiredField('Password'));
			return false;
		}

		setPasswordError('');
		return true;
	};

	const loginAction = (event: FormEvent) => {
		event.preventDefault();
		const isEmailValid = validateEmail();
		const isPasswordValid = validatePassword();

		if (!isEmailValid || !isPasswordValid) {
			return;
		}

		onLogin({
			request: {
				request: { email, password },
				formKey: LOGIN_FORM_KEY,
			},
		});
	};

	return (
		<Box component="section" role="main">
			<AuthLayout>
				<AuthCardLayout title="Welcome!">
					<Box component="form" onSubmit={loginAction} mt={3}>
						<Stack direction="column" spacing={3}>
							<Stack direction="column" spacing={2}>
								<TextField
									id="email"
									name="email"
									aria-label="Email"
									label="Email"
									type="email"
									formKey={LOGIN_FORM_KEY}
									fullWidth
									value={email}
									onChange={(event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
									clientErrors={emailError}
								/>
								<PasswordField
									id="password"
									name="password"
									aria-label="Password"
									label="Password"
									formKey={LOGIN_FORM_KEY}
									required
									fullWidth
									value={password}
									onChange={(event: ChangeEvent<HTMLInputElement>) =>
										setPassword(event.target.value)
									}
									clientErrors={passwordError}
								/>
								<Link component={RouterLink} to="/auth/forgot-password">
									Forgot Password?
								</Link>
							</Stack>

							<LoadingButton
								loading={isLoading}
								variant="contained"
								size="large"
								fullWidth
								ariaLabel="Login progress"
								type="submit"
							>
								Log In
							</LoadingButton>
						</Stack>
					</Box>
				</AuthCardLayout>
			</AuthLayout>
		</Box>
	);
};

export default Login;
